import React from 'react';
import classNames from 'classnames';
import { EMPTY_SPACE } from 'twain-sport-fe-mono/constants/textSymbols';
import { formatStatisticsValue } from 'twain-sport-fe-mono/utils/formatStatisticsValue';
import { IStatistics, IParticipantListItem } from 'twain-sport-fe-mono/interfaces';
import { translationProvider } from 'twain-sport-fe-mono/store/translations';
import { RepeatCircleIcon } from 'twain-sport-fe-mono/icons';
import { TooltipPosition } from 'twain-sport-fe-mono/components/Tooltip';
import { TableTooltip } from 'twain-sport-fe-mono/components/TableTooltip';
import { Avatar } from 'twain-sport-fe-mono/components/Avatar';
import classes from './ParticipantRow.module.scss';

interface IProps {
    participant: IParticipantListItem;
    index: number;
    mainStatistics?: string[];
    className?: string;
    onActionClick?(): void;
    renderAction(): React.ReactNode;
    forceFormatStatsValue?: boolean;
    navigate?: () => void;
    hideRank?: boolean;
}

const formatStatValue = (value: IStatistics, forceFormatStatsValue: boolean): string =>
    forceFormatStatsValue || value.value ? formatStatisticsValue(value) : '-';

const defaultProps: Partial<IProps> = {
    forceFormatStatsValue: false,
};

export const ParticipantRow: React.FC<IProps> = (props) => {
    const statistics = props.mainStatistics
        ? props.mainStatistics
              .map((value) =>
                  props.participant.statistics.find((statistic) => value === statistic.name),
              )
              .filter(Boolean)
        : props.participant.statistics;

    const onParticipantClick = React.useCallback(
        (event: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
            event.stopPropagation();

            if (props.navigate) {
                props.navigate();
            }
        },
        [props.navigate],
    );

    return (
        <tr
            className={classNames(classes.container, props.className)}
            onClick={props.onActionClick}
        >
            <td className={classes.index}>{props.index}</td>
            <td role="presentation" className={classes.avatar} onClick={onParticipantClick}>
                <Avatar src={props.participant.avatarPictureUrl} size="small" />
            </td>
            <td role="presentation" onClick={onParticipantClick}>
                <div className={classes.nameContent}>
                    <span className={classes.nickname}>{props.participant.nickname}</span>
                    {!props.hideRank && (
                        <>
                            {EMPTY_SPACE}
                            <span className={classes.rank}>({props.participant.rank ?? '-'})</span>
                        </>
                    )}
                    {props.participant.isReplaced && (
                        <>
                            {EMPTY_SPACE}
                            <div className={classes.tooltipContainer}>
                                <TableTooltip
                                    tooltipText={translationProvider.string('player_was_changed')}
                                    position={TooltipPosition.RIGHT}
                                    className={classes.tooltip}
                                >
                                    <RepeatCircleIcon className={classes.repeatIcon} />
                                </TableTooltip>
                            </div>
                        </>
                    )}
                </div>
            </td>
            {statistics.map((statistic) => (
                <td
                    key={statistic.name}
                    className={classes.stat}
                    data-qa="title-participants-statistic-value"
                >
                    {formatStatValue(statistic, props.forceFormatStatsValue)}
                </td>
            ))}
            <td className={classes.action}>{props.renderAction()}</td>
        </tr>
    );
};

ParticipantRow.defaultProps = defaultProps;
